import { supabase, supabaseConfigured } from './supabaseClient'
import {
  getQueue,
  removeMutation,
  incrementAttempts,
  subscribeQueueChanges,
} from './mutationQueue'
import { flushPhotoUploads } from './photoSync'

const MAX_ATTEMPTS = 5

let initialized = false
let flushing = false

async function applyMutation(m) {
  if (!supabaseConfigured) throw new Error('Supabase not configured')

  if (m.op === 'upsert') {
    const opts = m.conflictKey ? { onConflict: m.conflictKey } : undefined
    const { error } = await supabase.from(m.table).upsert(m.payload, opts)
    if (error) throw error
    return
  }
  if (m.op === 'update') {
    const { id, ...changes } = m.payload
    const { error } = await supabase.from(m.table).update(changes).eq('id', id)
    if (error) throw error
    return
  }
  if (m.op === 'delete') {
    const { error } = await supabase.from(m.table).delete().eq('id', m.payload.id)
    if (error) throw error
    return
  }
  throw new Error(`Unknown mutation op: ${m.op}`)
}

export async function flush() {
  if (flushing) return
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return
  flushing = true
  try {
    const queue = await getQueue()
    let synced = 0
    for (const m of queue) {
      if ((m.attempts || 0) >= MAX_ATTEMPTS) continue
      try {
        await applyMutation(m)
        await removeMutation(m.id)
        synced += 1
      } catch (err) {
        await incrementAttempts(m.id, err?.message || String(err))
        // Keep going — other mutations might still succeed
      }
    }
    if (synced > 0 && typeof window !== 'undefined') {
      window.dispatchEvent(new Event('phoenix:refresh'))
    }
  } finally {
    flushing = false
  }

  // Drain offline-queued photo uploads on the same trigger. Its own
  // re-entrancy guard prevents overlap; awaiting it here means a single
  // flush() call cleans both queues before returning.
  try {
    await flushPhotoUploads()
  } catch {
    /* photoSync swallows its own errors per-photo; no cross-cutting recovery needed */
  }
}

export async function retryStuckMutation(id) {
  const queue = await getQueue()
  const m = queue.find((x) => x.id === id)
  if (!m) return
  try {
    await applyMutation(m)
    await removeMutation(id)
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('phoenix:refresh'))
    }
  } catch (err) {
    await incrementAttempts(id, err?.message || String(err))
  }
}

export function initSyncEngine() {
  if (initialized) return
  initialized = true

  if (typeof window === 'undefined') return

  // Trigger flush on relevant events
  window.addEventListener('online', flush)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') flush()
  })
  window.addEventListener('phoenix:refresh', flush)

  // Auto-flush whenever the queue grows (e.g. user makes a write while online)
  subscribeQueueChanges(() => {
    flush()
  })

  // Initial pass — drain any mutations left over from a prior offline session
  flush()
}
